import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Alert, ButtonStyledLikeLink, Icon, Link, Spinner } from 'xm-spark';

import { showHelp } from 'actions/helpPopover';
import useAction from 'hooks/useAction';
import { useHelpLinks } from 'Application/HelpLinks/HelpLinksProvider';
import { XMATTERS_URL } from '../../../xm-constants/urls';

import { triggerRelayouts } from '../utils';
import HelpSearch from './HelpSearch';
import HelpViewPageError from './HelpViewPageError';
import QuickLinks from './QuickLinks';
import HelpViewIframe, { HelpViewIframeErrorBoundary } from './HelpViewIframe';
import styles from './HelpPage.module.scss';

const HOME_REGEX = /^https?:\/\/www.xmatters.com\//g;
const HOME_PROXY_PATH = '/webapi/xmHome/';

const HELP_REGEX = /^https?:\/\/help.xmatters.com\//g;
const PROXY_PATH = '/webapi/help/';

const EBFD_HELP_REGEX = /^https?:\/\/help.everbridge.net\//g;
const EBFD_PROXY_PATH = '/webapi/ebfdHelp/';

const getExternalLinkHref = (helpLinks, iframeSrc, iframeWindowHrefRef) => {
  const iframeLocation = iframeWindowHrefRef.current ?? iframeSrc ?? '';

  let proxyPath = '';
  let homeLink = false;
  if (iframeLocation.includes(EBFD_PROXY_PATH)) {
    proxyPath = EBFD_PROXY_PATH;
  } else if (iframeLocation.includes(PROXY_PATH)) {
    proxyPath = PROXY_PATH;
  } else {
    proxyPath = HOME_PROXY_PATH;
    homeLink = true;
  }

  const linkPath = iframeLocation.split(proxyPath)[1];
  if (homeLink) return `${XMATTERS_URL}/${linkPath}`;
  return linkPath ? `${helpLinks.ROOT}/${linkPath}` : helpLinks.DEFAULT;
};

const getProxiedSrc = location =>
  location
    ?.replace(HELP_REGEX, PROXY_PATH)
    .replace(EBFD_HELP_REGEX, EBFD_PROXY_PATH)
    .replace(HOME_REGEX, HOME_PROXY_PATH);

export default function HelpPage() {
  const { helpLinks } = useHelpLinks();
  const contentRef = useRef();
  const iframeWindowHrefRef = useRef();

  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const setHelpLocation = useAction(showHelp);

  const { refreshButtonLocation, helpLocation, helpUpdatedAt } = useSelector(
    state => state.helpPopover,
  );

  useEffect(() => {
    triggerRelayouts();
    // Trigger on unmount (so, on closing the Help menu);
    return triggerRelayouts;
  }, []);

  // Prep page location for viewing, including proxying help docs through webui to get around
  // X-FRAME-ORIGIN limitations blocking iframes:
  const iframeSrc = getProxiedSrc(helpLocation);
  const linkHref = getExternalLinkHref(
    helpLinks,
    iframeSrc,
    iframeWindowHrefRef,
  );

  const onIframeError = useCallback(() => {
    setHasLoadingError(true);
    setIsLoading(false);
  }, [setIsLoading, setHasLoadingError]);

  const onViewCurrentPageHelpClick = useCallback(() => {
    setHelpLocation(refreshButtonLocation);
  }, [refreshButtonLocation, setHelpLocation]);

  return (
    <>
      <HelpSearch
        contentRef={contentRef}
        iframeSrc={iframeSrc}
        setHelpLocation={setHelpLocation}
      />
      <div className={styles.helpContainer} ref={contentRef}>
        {refreshButtonLocation && (
          <Alert
            className={styles.currentPageChangeAlert}
            data-testid="HelpView_currentPageChanged"
          >
            <FormattedMessage id="HELP_MENU_CURRENT_PAGE_CHANGED" />
            <ButtonStyledLikeLink
              onClick={onViewCurrentPageHelpClick}
              data-testid="HelpView_currentPageChanged_button"
            >
              <FormattedMessage id="REFRESH" />
            </ButtonStyledLikeLink>
          </Alert>
        )}
        <div className={styles.helpPageContent}>
          <HelpViewIframeErrorBoundary onError={onIframeError}>
            <HelpViewIframe
              // UX and PM want each click on a help button to explicitly reload the frame,
              // even if already viewing the same page:
              key={helpUpdatedAt}
              iframeWindowHrefRef={iframeWindowHrefRef}
              iframeSrc={iframeSrc}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              setHasLoadingError={setHasLoadingError}
            />
          </HelpViewIframeErrorBoundary>
          {hasLoadingError && (
            <HelpViewPageError
              className={styles.loadingError}
              linkHref={linkHref}
            />
          )}
          {isLoading && (
            <div className={styles.spinner} data-testid="HelpView_loading">
              <Spinner />
            </div>
          )}
        </div>
      </div>
      <div className={styles.openInNewTabWrapper}>
        <Link
          href={linkHref}
          className={styles.openInNewTabLink}
          data-testid="HelpView_footer_openInNewTab"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon type="icon-link-widget" className={styles.openInNewTabIcon} />
          <FormattedMessage id="HELP_MENU_OPEN_IN_NEW_TAB" />
        </Link>
      </div>
      <QuickLinks />
    </>
  );
}

HelpPage.displayName = 'HelpPage';
